import type {
  NationCapacity,
  ResourceStockpile,
} from '../core/economyTypes'
import type { FacilityId } from './facilities'
import type { SpaceMissionId } from './spaceMissions'
import type { TechnologyId } from './technologies'
import type { MajorPowerId } from './regions'

export interface FacilityNationModifier {
  output?: Partial<ResourceStockpile>
  capacity?: Partial<NationCapacity>
}

export interface NationConstructionPlanItem {
  regionId: string
  facilityId: FacilityId
}

export interface NationProfile {
  id: MajorPowerId
  doctrineName: string
  summary: string
  effectLabels: string[]
  facilityModifiers: Partial<
    Record<FacilityId, FacilityNationModifier>
  >
  missionTurnModifiers: Partial<Record<SpaceMissionId, number>>
  aiResearchPriorities: TechnologyId[]
  aiIntenseResearchPriorities: TechnologyId[]
  // Map-independent construction priorities for a future scenario with new regions.
  aiFacilityPriorities: FacilityId[]
  aiIntenseFacilityPriorities: FacilityId[]
  aiConstructionPlan: NationConstructionPlanItem[]
  aiIntenseConstructionPlan: NationConstructionPlanItem[]
}

export const NATION_PROFILES: Record<MajorPowerId, NationProfile> = {
  starsea: {
    id: 'starsea',
    doctrineName: '系统工程与电子集成',
    summary:
      '强调电子工业、科研组织和复杂航天系统的集成能力，前期并不直接获得免费资源，但投资科研与电子设施的长期收益更高。',
    effectLabels: [
      '研究院：额外 +1 科研能力',
      '电子厂：额外 +1 电子元件/回合',
      '绕月验证：准备时间 -1 回合',
      '载人登月：准备时间 -1 回合',
    ],
    facilityModifiers: {
      'research-institute': {
        capacity: { research: 1 },
      },
      'electronics-plant': {
        output: { electronics: 1 },
      },
    },
    missionTurnModifiers: {
      'lunar-validation': -1,
      'crewed-lunar-landing-return': -1,
    },
    aiResearchPriorities: [
      'vacuum-electronics',
      'industrial-standardization',
      'radar-tracking',
      'liquid-rocketry',
      'orbital-flight',
      'life-support',
      'inertial-guidance',
      'multistage-rockets',
      'crewed-spacecraft',
      'jet-propulsion',
    ],
    aiIntenseResearchPriorities: [
      'industrial-standardization',
      'liquid-rocketry',
      'vacuum-electronics',
      'radar-tracking',
      'multistage-rockets',
      'orbital-flight',
      'life-support',
      'inertial-guidance',
      'crewed-spacecraft',
      'jet-propulsion',
    ],
    aiFacilityPriorities: [
      'research-institute', 'electronics-plant', 'refinery', 'power-plant', 'mine',
    ],
    aiIntenseFacilityPriorities: [
      'research-institute', 'electronics-plant', 'power-plant', 'refinery', 'mine',
    ],
    aiConstructionPlan: [
      { regionId: 'star-east', facilityId: 'research-institute' },
      { regionId: 'star-west', facilityId: 'electronics-plant' },
      { regionId: 'star-south', facilityId: 'refinery' },
      { regionId: 'star-central', facilityId: 'power-plant' },
      { regionId: 'star-central', facilityId: 'mine' },
    ],
    aiIntenseConstructionPlan: [
      { regionId: 'star-east', facilityId: 'research-institute' },
      { regionId: 'star-west', facilityId: 'electronics-plant' },
      { regionId: 'star-central', facilityId: 'power-plant' },
      { regionId: 'star-south', facilityId: 'refinery' },
      { regionId: 'star-central', facilityId: 'mine' },
    ],
  },
  redplain: {
    id: 'redplain',
    doctrineName: '重型运载与早期突破',
    summary:
      '强调重工业、燃料供给和尽早取得航天里程碑，在早期运载与载人轨道阶段准备更快，但后期复杂登月工程没有额外时间优势。',
    effectLabels: [
      '矿冶厂：额外 +1 金属/回合',
      '燃料厂：额外 +1 燃料/回合',
      '首颗人造卫星：准备时间 -1 回合',
      '载人地球轨道：准备时间 -1 回合',
    ],
    facilityModifiers: {
      mine: {
        output: { metal: 1 },
      },
      refinery: {
        output: { fuel: 1 },
      },
    },
    missionTurnModifiers: {
      'first-satellite': -1,
      'crewed-orbit': -1,
    },
    aiResearchPriorities: [
      'industrial-standardization',
      'liquid-rocketry',
      'vacuum-electronics',
      'radar-tracking',
      'multistage-rockets',
      'orbital-flight',
      'inertial-guidance',
      'life-support',
      'crewed-spacecraft',
      'jet-propulsion',
    ],
    aiIntenseResearchPriorities: [
      'industrial-standardization',
      'liquid-rocketry',
      'vacuum-electronics',
      'radar-tracking',
      'multistage-rockets',
      'orbital-flight',
      'life-support',
      'inertial-guidance',
      'crewed-spacecraft',
      'jet-propulsion',
    ],
    aiFacilityPriorities: [
      'mine', 'research-institute', 'refinery', 'electronics-plant', 'power-plant',
    ],
    aiIntenseFacilityPriorities: [
      'research-institute', 'mine', 'refinery', 'electronics-plant', 'power-plant',
    ],
    aiConstructionPlan: [
      { regionId: 'red-central', facilityId: 'mine' },
      { regionId: 'red-north', facilityId: 'research-institute' },
      { regionId: 'red-east', facilityId: 'refinery' },
      { regionId: 'red-north', facilityId: 'electronics-plant' },
      { regionId: 'red-west', facilityId: 'power-plant' },
    ],
    aiIntenseConstructionPlan: [
      { regionId: 'red-north', facilityId: 'research-institute' },
      { regionId: 'red-central', facilityId: 'mine' },
      { regionId: 'red-east', facilityId: 'refinery' },
      { regionId: 'red-north', facilityId: 'electronics-plant' },
      { regionId: 'red-west', facilityId: 'power-plant' },
    ],
  },
  zhonghua: {
    id: 'zhonghua',
    doctrineName: '工业协同与渐进航天',
    summary: '以标准化制造与科研协同支撑逐步扩展的载人航天计划；当前剧本未激活。',
    effectLabels: [
      '矿冶厂：额外 +1 金属/回合',
      '研究院：额外 +1 科研能力',
      '载人地球轨道：准备时间 -1 回合',
      '绕月验证：准备时间 -1 回合',
    ],
    facilityModifiers: {
      mine: { output: { metal: 1 } },
      'research-institute': { capacity: { research: 1 } },
    },
    missionTurnModifiers: {
      'crewed-orbit': -1,
      'lunar-validation': -1,
    },
    aiResearchPriorities: [
      'industrial-standardization', 'liquid-rocketry', 'vacuum-electronics',
      'radar-tracking', 'life-support', 'multistage-rockets', 'orbital-flight',
      'inertial-guidance', 'crewed-spacecraft', 'jet-propulsion',
    ],
    aiIntenseResearchPriorities: [
      'industrial-standardization', 'vacuum-electronics', 'liquid-rocketry',
      'radar-tracking', 'life-support', 'orbital-flight', 'multistage-rockets',
      'inertial-guidance', 'crewed-spacecraft', 'jet-propulsion',
    ],
    aiFacilityPriorities: [
      'mine', 'research-institute', 'power-plant', 'refinery', 'electronics-plant',
    ],
    aiIntenseFacilityPriorities: [
      'research-institute', 'mine', 'power-plant', 'electronics-plant', 'refinery',
    ],
    // Region targets are assigned when a future map includes this nation.
    aiConstructionPlan: [],
    aiIntenseConstructionPlan: [],
  },
  britannia: {
    id: 'britannia',
    doctrineName: '航空电子与精密测控',
    summary: '以航空电子和测控网络支持卫星与远程任务；当前剧本未激活。',
    effectLabels: [
      '电子厂：额外 +1 电子元件/回合',
      '发电站：额外 +1 电力能力',
      '首颗人造卫星：准备时间 -1 回合',
      '绕月验证：准备时间 -1 回合',
    ],
    facilityModifiers: {
      'electronics-plant': { output: { electronics: 1 } },
      'power-plant': { capacity: { power: 1 } },
    },
    missionTurnModifiers: {
      'first-satellite': -1,
      'lunar-validation': -1,
    },
    aiResearchPriorities: [
      'vacuum-electronics', 'industrial-standardization', 'radar-tracking',
      'jet-propulsion', 'liquid-rocketry', 'inertial-guidance',
      'orbital-flight', 'multistage-rockets', 'life-support', 'crewed-spacecraft',
    ],
    aiIntenseResearchPriorities: [
      'vacuum-electronics', 'industrial-standardization', 'radar-tracking',
      'liquid-rocketry', 'orbital-flight', 'multistage-rockets',
      'inertial-guidance', 'life-support', 'crewed-spacecraft', 'jet-propulsion',
    ],
    aiFacilityPriorities: [
      'electronics-plant', 'research-institute', 'power-plant', 'refinery', 'mine',
    ],
    aiIntenseFacilityPriorities: [
      'research-institute', 'electronics-plant', 'power-plant', 'refinery', 'mine',
    ],
    aiConstructionPlan: [],
    aiIntenseConstructionPlan: [],
  },
  francia: {
    id: 'francia',
    doctrineName: '独立科研与推进技术',
    summary: '以推进试验和自主发射能力带动航天科研；当前剧本未激活。',
    effectLabels: [
      '燃料厂：额外 +1 燃料/回合',
      '研究院：额外 +1 科研能力',
      '首颗人造卫星：准备时间 -1 回合',
      '载人登月：准备时间 -1 回合',
    ],
    facilityModifiers: {
      refinery: { output: { fuel: 1 } },
      'research-institute': { capacity: { research: 1 } },
    },
    missionTurnModifiers: {
      'first-satellite': -1,
      'crewed-lunar-landing-return': -1,
    },
    aiResearchPriorities: [
      'industrial-standardization', 'liquid-rocketry', 'vacuum-electronics',
      'multistage-rockets', 'radar-tracking', 'inertial-guidance',
      'orbital-flight', 'jet-propulsion', 'life-support', 'crewed-spacecraft',
    ],
    aiIntenseResearchPriorities: [
      'industrial-standardization', 'liquid-rocketry', 'vacuum-electronics',
      'radar-tracking', 'multistage-rockets', 'orbital-flight',
      'inertial-guidance', 'life-support', 'crewed-spacecraft', 'jet-propulsion',
    ],
    aiFacilityPriorities: [
      'refinery', 'research-institute', 'electronics-plant', 'power-plant', 'mine',
    ],
    aiIntenseFacilityPriorities: [
      'research-institute', 'refinery', 'electronics-plant', 'power-plant', 'mine',
    ],
    aiConstructionPlan: [],
    aiIntenseConstructionPlan: [],
  },
}

export function getNationProfile(
  nationId: MajorPowerId,
): NationProfile {
  return NATION_PROFILES[nationId]
}
